/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.18.0
 */

import React, { ChangeEvent, FormEvent, forwardRef, Fragment, useCallback, useEffect, useState } from 'react';

import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import ButtonServiceUserDenied from '@/main/modules/role/components/ButtonServiceUserDenied';
import FormFieldInput from '@modules/form/components/FormFieldInput';
import FormFieldSelect from '@modules/form/components/FormFieldSelect';
import FormFieldTextarea from '@modules/form/components/FormFieldTextarea';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ECalendarPeriod } from '@modules/internationalization/types/common/calendar';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import { stringFormatParamsHtml } from '@utils/stringFormat';

import { getTimeZone } from '../../Table/helpers/getTimeZone';
import { FormBlockStyled, FormControlStyled, FormLabelStyled } from '../FormStyled/FormStyled';
import { LinkBlock } from '../index';
import type { IOptionItemProps } from '../Select/Select';
import { hourOptions, minuteOptions, ReportScheduleValidationSchema } from './constants';
import FormFieldWeek from './FormFieldWeek';
import { ReportScheduleStyled } from './styled/ReportScheduleStyled';
import { EReportScheduleField, EReportScheduleField as F, IReportScheduleData } from './types';

interface IReportSchedule {
    initialValue: IReportScheduleData;
    isNewSubscribersReport?: boolean;
    onSubmit?: (data: IReportScheduleData) => void;
    onChangeDescription?: (
        event: React.FormEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLTextAreaElement>,
    ) => void;
    onChangeMonth?: (event: FormEvent<HTMLInputElement> | ChangeEvent<HTMLInputElement>) => void;
    onSelectHour?: (data: IOptionItemProps) => void;
    onSelectMinutes?: (data: IOptionItemProps) => void;
    onSelectPeriod?: (data: IOptionItemProps) => void;
    onSelectWeek?: (data: IOptionItemProps) => void;
}

const ReportSchedule = forwardRef<FormikProps<IReportScheduleData>, IReportSchedule>(
    (
        {
            initialValue,
            isNewSubscribersReport,
            onSubmit,
            onChangeDescription,
            onChangeMonth,
            onSelectHour,
            onSelectMinutes,
            onSelectPeriod,
            onSelectWeek,
        },
        ref,
    ) => {
        const {
            calendar: tCalender,
            form: tForm,
            reportSchedule: tReportSchedule,
        } = useSelector(makeSelectTranslationCommon());

        const periodOptions: IOptionItemProps[] = [
            {
                value: ECalendarPeriod.M,
                label: tCalender.period[ECalendarPeriod.M],
                dataName: 'report-schedule-period-option-month',
            },
            {
                value: ECalendarPeriod.W,
                label: tCalender.period[ECalendarPeriod.W],
                dataName: 'report-schedule-period-option-week',
            },
            {
                value: ECalendarPeriod.D,
                label: tCalender.period[ECalendarPeriod.D],
                dataName: 'report-schedule-period-option-day',
            },
        ];

        const weekOptions: IOptionItemProps[] = Array.from(Array(7)).map((_, i) => ({
            value: i,
            label: tCalender.weeks[i],
        }));

        const [validationSchema, setValidationSchema] = useState<Yup.AnySchema>();

        useEffect(() => {
            setValidationSchema(ReportScheduleValidationSchema(tForm.validation));
        }, [tForm.validation]);

        const onSubmitHandler = useCallback(
            (data: IReportScheduleData) => {
                onSubmit?.(data);
            },
            [onSubmit],
        );

        return (
            <Formik
                enableReinitialize
                initialValues={{
                    ...initialValue,
                    [F.Period]:
                        periodOptions.find((v) => v.value === initialValue[F.Period]?.value) || periodOptions[2],
                    [F.TimeHour]: hourOptions.find(
                        (v) =>
                            v.value ===
                            Number(moment(initialValue[EReportScheduleField.StartTime]).format(EFormatData.OnlyHour)),
                    ),
                    [F.TimeMinute]: minuteOptions.find(
                        (v) =>
                            v.value ===
                            Number(
                                moment(initialValue[EReportScheduleField.StartTime]).format(EFormatData.OnlyMinutes),
                            ),
                    ),
                    [F.Week]: weekOptions.find((v) => v.value === initialValue[F.Week]?.value),
                }}
                innerRef={ref}
                validationSchema={validationSchema}
                onSubmit={onSubmitHandler}
            >
                {({ dirty, getFieldProps, isValid, isSubmitting, resetForm, validateField, submitForm }) => (
                    <ReportScheduleStyled>
                        {/* Тип расписания */}
                        <FormFieldSelect
                            dataName="report-schedule-period"
                            label={isNewSubscribersReport
                                ? tReportSchedule.period.labelNewSubscribers
                                : tReportSchedule.period.label}
                            name={F.Period}
                            options={periodOptions}
                            placeholder=""
                            onSelect={(value) => {
                                onSelectPeriod?.(value);
                                setTimeout(() => {
                                    validateField(F.Month);
                                    validateField(F.Week);
                                });
                            }}
                        />
                        {
                            {
                                [ECalendarPeriod.M]: (
                                    <FormBlockStyled>
                                        {
                                            isNewSubscribersReport && <div className="report-schedule__prompt">
                                                {tReportSchedule.period[ECalendarPeriod.M].newSubscribersPrompt}
                                            </div>
                                        }
                                        <div className="report-schedule__row">
                                            {stringFormatParamsHtml(tReportSchedule.period[ECalendarPeriod.M].label, {
                                                [ELocaleKeys.number]: (
                                                    <FormFieldInput
                                                        dataName="report-schedule-period-month"
                                                        name={F.Month}
                                                        onChange={onChangeMonth}
                                                    />
                                                ),
                                            })}
                                        </div>
                                        <div className="report-schedule__prompt">
                                            {tReportSchedule.period[ECalendarPeriod.M].prompt}
                                        </div>
                                    </FormBlockStyled>
                                ),
                                [ECalendarPeriod.W]: (
                                    <Fragment>
                                        {
                                            isNewSubscribersReport && <div className="report-schedule__prompt">
                                                {tReportSchedule.period[ECalendarPeriod.W].newSubscribersPrompt}
                                            </div>
                                        }
                                        <FormLabelStyled>
                                            {tReportSchedule.period[ECalendarPeriod.W].label}
                                        </FormLabelStyled>
                                        <FormFieldWeek
                                            dataName="report-schedule-period-week"
                                            name={F.Week}
                                            weekOptions={weekOptions}
                                            onSelectWeek={(value) => {
                                                onSelectWeek?.(value);
                                            }}
                                        />
                                    </Fragment>
                                ),
                                [ECalendarPeriod.D]: isNewSubscribersReport ? (
                                    <Fragment>
                                        <div className="report-schedule__prompt">
                                            {tReportSchedule.period[ECalendarPeriod.D].newSubscribersPrompt}
                                        </div>
                                    </Fragment>
                                ) : null,
                            }[getFieldProps(F.Period).value.value as ECalendarPeriod]
                        }

                        {/* Время формирования отчета */}
                        <FormBlockStyled>
                            <FormLabelStyled>{tReportSchedule.time.label}</FormLabelStyled>
                            <div className="report-schedule__row">
                                <FormFieldSelect
                                    dataName="report-schedule-time-hour"
                                    name={F.TimeHour}
                                    options={hourOptions}
                                    placeholder={tReportSchedule.time.hours}
                                    onSelect={(value) => {
                                        onSelectHour?.(value);
                                    }}
                                />
                                :
                                <FormFieldSelect
                                    dataName="report-schedule-time-minute"
                                    name={F.TimeMinute}
                                    options={minuteOptions}
                                    placeholder={tReportSchedule.time.minutes}
                                    onSelect={(value) => {
                                        onSelectMinutes?.(value);
                                    }}
                                />
                                <span data-name="report-timezone">{getTimeZone(new Date().toISOString())}</span>
                            </div>
                            <div className="report-schedule__prompt">{tReportSchedule.time.prompt}</div>
                        </FormBlockStyled>

                        {/* Описание */}
                        <FormFieldTextarea
                            dataName="report-schedule-description"
                            label={tReportSchedule.description}
                            maxLength={512}
                            name={F.Description}
                            rows={6}
                            onChangeTextarea={onChangeDescription}
                        />

                        {onSubmit && (
                            <FormControlStyled>
                                <ButtonServiceUserDenied
                                    dataName="format-delivery-button-save"
                                    disabled={!isValid || isSubmitting || !dirty}
                                    kind="medium"
                                    type="submit"
                                    onClick={submitForm}
                                >
                                    {tForm.save}
                                </ButtonServiceUserDenied>
                                <LinkBlock dataName="cancelButton" disabled={!dirty} onClick={() => resetForm()}>
                                    {tForm.cancel}
                                </LinkBlock>
                            </FormControlStyled>
                        )}
                    </ReportScheduleStyled>
                )}
            </Formik>
        );
    },
);

export default ReportSchedule;
